import React, { useCallback, useMemo, useState } from 'react';
import { Container } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useHistory } from 'react-router-dom';
import { MainLayoutContainer, ProviderCancelSessionModal, ProviderLayout, UpliftThemeProvider } from '@uplift/ui-v2';
import { useQuery } from '@tanstack/react-query';
import { unsubmittedSessionsRequest } from '@uplift/api';
import { providerSubmitSessionRoute, ROUTING_CONFIG } from '@uplift/constants';
import { convertUnsubmittedSessionsToRows, getHasExpiredSession } from './helpers';
import { UnsubmittedSessionsContext } from './context';
import { UnsubmittedSessionsTable } from './components/unsubmitted-sessions-table/unsubmitted-sessions-table.component';
import { UnsubmittedSessionsCards } from './components/unsubmitted-sessions-cards/unsubmitted-sessions-cards.component';
import { UnsubmittedSessionsHeader } from './components/unsubmitted-sessions-header.component';
import { getUserFullName } from '@uplift/utils';
import { useUpBreakpoint } from '@uplift/hooks';

export const useStyles = makeStyles()((theme) => ({
  breadcrumb: {
    color: theme.palette.text.primary,
    '&:visited': {
      color: theme.palette.text.primary
    }
  },
  nextBreadcrumb: {
    color: theme.palette.text.disabled,
  },
  title: {
    color: theme.palette.darkSlate.main,
    marginBottom: 0,
    fontSize: '36px',
    [theme.breakpoints.up('xs')]: {
      fontSize: '34px'
    }
  },
  alertTitle: {
    fontWeight: 700
  }
}));

export const UnsubmittedSessionsPage: React.FC = () => {
  const history = useHistory();
  const isSmUp = useUpBreakpoint('sm');
  const [ cancelSessionModal, setCancelSessionModal ] = useState<string | null>(null); // used for modal visibility and to store appointment id

  const { data: unsubmittedSessions = [], isLoading, refetch } = useQuery({
    queryKey: [ 'unsubmitted-sessions' ],
    queryFn: async () => {
      const response = await unsubmittedSessionsRequest(null);

      return response.data;
    }
  });

  const rows = useMemo(() => convertUnsubmittedSessionsToRows(unsubmittedSessions), [ unsubmittedSessions ]);
  const hasExpiredSession = useMemo(() => getHasExpiredSession(unsubmittedSessions), [ unsubmittedSessions ]);

  const onSubmitSession = useCallback((appointmentId: string) => {
    history.push(
      providerSubmitSessionRoute.createLink(
        { appointmentId },
        { redirect_to: ROUTING_CONFIG.notSubmittedSessions }
      )
    );
  }, [ history ]);

  const onToggleCancelSessionModal = useCallback((appointmentId: string) => setCancelSessionModal(appointmentId), []);

  const modal = useMemo(() => {
    if (!cancelSessionModal) return null;

    const session = unsubmittedSessions.find(({ appointment: { id } }) => id === cancelSessionModal);

    return (
      <ProviderCancelSessionModal
        open
        onClose={() => setCancelSessionModal(null)}
        appointmentId={session.appointment.id}
        fromDatetime={session.appointment.fromDatetime}
        // Do not pass Parent ID to prevent cancelling entire series on unsuibmitted sessions page
        // parentId={session.appointment.appointmentSeriesId}
        onSubmit={async () => {
          refetch();
        }}
        patientDisplayName={getUserFullName(session.patient.firstName, session.patient.lastName, session.patientProfile.preferredName)}
        patientId={session.appointment.patientId}
        patientStatus={session.patient.status}
      />
    );
  }, [ cancelSessionModal, refetch, unsubmittedSessions ]);

  return (
    <ProviderLayout
      loading={isLoading}
      // TODO: add this behavior in Responsive PR
      // overrideMenuIconClick={isMobile ? onMobileMenuIconClick : undefined}
    >
      <MainLayoutContainer>
        <UpliftThemeProvider>
          <Container disableGutters>

            <UnsubmittedSessionsHeader unsubmittedSessionsLength={unsubmittedSessions.length} hasExpiredSession={hasExpiredSession} />

            <UnsubmittedSessionsContext.Provider value={{ onSubmitSession, onToggleCancelSessionModal }}>
              {isSmUp ? (
                <UnsubmittedSessionsTable rows={rows} />
              ) : (
                <UnsubmittedSessionsCards rows={rows} />
              )}
            </UnsubmittedSessionsContext.Provider>

            {modal}

          </Container>
        </UpliftThemeProvider>
      </MainLayoutContainer>
    </ProviderLayout>
  );
};
